import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Table,
  Tag,
  Space,
  Avatar,
  Tooltip,
  message,
  Empty,
} from 'antd';
import {
  EditOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Patient, PatientVisit } from '../../types/patient';
import { patientService } from '../../services/patientService';

const { Title, Text } = Typography;

interface PatientDetailsProps {
  patient: Patient;
  onEdit: () => void;
  onClose: () => void;
}

const PatientDetails: React.FC<PatientDetailsProps> = ({ patient, onEdit, onClose }) => {
  const [visits, setVisits] = useState<PatientVisit[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPatientVisits();
  }, [patient.id]);

  const fetchPatientVisits = async () => {
    setLoading(true);
    try {
      const data = await patientService.getPatientVisits(patient.id);
      setVisits(data);
    } catch (error) {
      message.error('Failed to fetch patient visits');
      console.error('Error fetching patient visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPatientAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getInitials = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };



  const visitColumns = [
    {
      title: 'Visit Date',
      dataIndex: 'visit_date',
      key: 'visit_date',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a: PatientVisit, b: PatientVisit) => 
        dayjs(a.visit_date).unix() - dayjs(b.visit_date).unix(),
    },
    {
      title: 'Visit Type',
      dataIndex: 'visit_type',
      key: 'visit_type',
      render: (type: string) => type || 'General Visit',
    },
    {
      title: 'Chief Complaint',
      dataIndex: 'chief_complaint',
      key: 'chief_complaint',
      render: (complaint: string) => complaint || 'Not specified',
      ellipsis: true,
    },
    {
      title: 'Recording',
      key: 'recording',
      render: (record: PatientVisit) => (
        <div>
          <div className="text-sm font-medium">
            {record.recording?.title || 'Untitled Recording'}
          </div>
          <div className="text-xs text-gray-500">
            {record.recording?.duration && record.recording.duration > 0 
              ? `${Math.round(record.recording.duration / 60)} min` 
              : 'Duration unknown'}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: PatientVisit) => {
        const status = record.recording?.status || 'unknown';
        const color = status === 'completed' ? 'green' : 
                     status === 'processing' ? 'blue' : 
                     status === 'failed' ? 'red' : 'default';
        return <Tag color={color}>{status.charAt(0).toUpperCase() + status.slice(1)}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: PatientVisit) => (
        <Space size="small">
          <Tooltip title="View Recording">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              className="text-blue-600 hover:text-blue-800"
              onClick={() => {
                // Navigate to recording details or open in new tab
                window.open(`/mynotes?recording=${record.recording_id}`, '_blank');
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      {/* Patient Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <Avatar size={64} className="bg-blue-500">
              {getInitials(patient.name)}
            </Avatar>
            <div>
              <Title level={3} className="!mb-1">
                {patient.name}
              </Title>
              <Text className="text-gray-600">
                Patient ID: {patient.id}
              </Text>
              <br />
              <Text className="text-sm text-gray-500">
                Patient ID: {patient.id} • MRN: {patient.medical_record_number || 'Not assigned'}
              </Text>
            </div>
          </div>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={onEdit}
          >
            Edit Patient
          </Button>
        </div>
      </div>

      <Row gutter={16}>
        {/* Patient Information */}
        <Col xs={24} lg={14}>
          {/* Basic Information */}
          {patient.date_of_birth && (
            <Card title="Basic Information" className="mb-4">
              <div className="mb-3">
                <Text strong>Date of Birth:</Text>
                <br />
                <Text>{dayjs(patient.date_of_birth).format('MMMM DD, YYYY')}</Text>
              </div>
            </Card>
          )}

          {/* Contact Information */}
          <Card title="Contact Information" className="mb-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <MailOutlined className="text-gray-500" />
                <Text>{patient.email || 'No email provided'}</Text>
              </div>
              <div className="flex items-center space-x-2">
                <PhoneOutlined className="text-gray-500" />
                <Text>{patient.phone || 'No phone provided'}</Text>
              </div>
              {patient.address && (
                <div>
                  <Text strong>Address:</Text>
                  <br />
                  <Text>{patient.address}</Text>
                </div>
              )}
            </div>
          </Card>

          {/* Medical Information */}
          {patient.medical_record_number && (
            <Card title="Medical Information" className="mb-4">
              <div>
                <Text strong>Medical Record Number:</Text>
                <br />
                <Text>{patient.medical_record_number}</Text>
              </div>
            </Card>
          )}
        </Col>

        {/* Sidebar Information */}
        <Col xs={24} lg={10}>
          {/* Insurance Information */}
          {patient.insurance_info && (
            <Card title="Insurance Information" className="mb-4">
              <div>
                <Text strong>Insurance Details:</Text>
                <br />
                <Text>{patient.insurance_info}</Text>
              </div>
            </Card>
          )}

          {/* Additional Notes */}
          {patient.notes && (
            <Card title="Additional Notes" className="mb-4">
              <Text>{patient.notes}</Text>
            </Card>
          )}
        </Col>
      </Row>

      <Divider />

      {/* Visit History */}
      <Card
        title={
          <span>
            <CalendarOutlined className="mr-2" />
            Visit History ({visits.length})
          </span>
        }
        className="mb-4"
      >
        {visits.length > 0 ? (
          <Table
            columns={visitColumns}
            dataSource={visits}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
            }}
            size="small"
          />
        ) : (
          <Empty
            description="No visits recorded yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>


    </div>
  );
};

export default PatientDetails; 